/**
 * Centralized Error Handling Utility
 * 
 * Issue #7: Inconsistent Error Handling Across Features
 * 
 * Provides:
 * - Consistent error classification
 * - User-friendly error messages
 * - Structured error logging
 * - Error recovery suggestions
 * - Error reporting/telemetry hooks
 */

import { trackEvent } from './telemetry';

// Error severity levels
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

// Error categories
export enum ErrorCategory {
  API = 'api',
  NETWORK = 'network',
  VALIDATION = 'validation',
  OFFICE_JS = 'office_js',
  PARSING = 'parsing',
  TIMEOUT = 'timeout',
  AUTHENTICATION = 'authentication',
  RATE_LIMIT = 'rate_limit',
  UNKNOWN = 'unknown',
}

// Structured error interface
export interface AppError {
  message: string;
  userMessage: string;
  category: ErrorCategory;
  severity: ErrorSeverity;
  code?: string;
  status?: number;
  context?: string;
  timestamp: string;
  stack?: string;
  metadata?: Record<string, unknown>;
  recoverySuggestions?: string[];
}

/**
 * Creates a structured AppError from any error type
 */
export const createAppError = (
  error: unknown,
  context?: string,
  metadata?: Record<string, unknown>
): AppError => {
  const timestamp = new Date().toISOString();

  // Handle Error objects
  if (error instanceof Error) {
    const errorMsg = error.message.toLowerCase();

    // Classify error by message content
    let category = ErrorCategory.UNKNOWN;
    let severity = ErrorSeverity.MEDIUM;
    let userMessage = error.message;
    let recoverySuggestions: string[] = [];

    // Timeout errors
    if (errorMsg.includes('timeout') || errorMsg.includes('timed out') || errorMsg.includes('aborted')) {
      category = ErrorCategory.TIMEOUT;
      severity = ErrorSeverity.MEDIUM;
      userMessage = 'The request took too long to complete.';
      recoverySuggestions = [
        'Try selecting a smaller portion of text',
        'Simplify your request',
        'Check your internet connection',
        'Try again in a few moments',
      ];
    }
    // Network errors
    else if (errorMsg.includes('network') || errorMsg.includes('fetch') || errorMsg.includes('connection')) {
      category = ErrorCategory.NETWORK;
      severity = ErrorSeverity.HIGH;
      userMessage = 'Unable to connect to the service.';
      recoverySuggestions = [
        'Check your internet connection',
        'Verify you are not behind a firewall',
        'Try again in a few moments',
      ];
    }
    // Authentication errors
    else if (errorMsg.includes('api key') || errorMsg.includes('authentication') || errorMsg.includes('401') || errorMsg.includes('unauthorized')) {
      category = ErrorCategory.AUTHENTICATION;
      severity = ErrorSeverity.CRITICAL;
      userMessage = 'API authentication failed.';
      recoverySuggestions = [
        'Check your API key configuration',
        'Verify the API key is valid and not expired',
        'Contact your administrator',
      ];
    }
    // Rate limit errors
    else if (errorMsg.includes('rate limit') || errorMsg.includes('429') || errorMsg.includes('too many requests')) {
      category = ErrorCategory.RATE_LIMIT;
      severity = ErrorSeverity.MEDIUM;
      userMessage = 'Too many requests. Please slow down.';
      recoverySuggestions = [
        'Wait a few moments before trying again',
        'Reduce the frequency of requests',
      ];
    }
    // Validation errors
    else if (errorMsg.includes('invalid') || errorMsg.includes('validation') || errorMsg.includes('required')) {
      category = ErrorCategory.VALIDATION;
      severity = ErrorSeverity.LOW;
      userMessage = 'Invalid input provided.';
      recoverySuggestions = [
        'Check your input and try again',
        'Ensure all required fields are filled',
      ];
    }
    // Parsing errors
    else if (errorMsg.includes('parse') || errorMsg.includes('json') || errorMsg.includes('syntax')) {
      category = ErrorCategory.PARSING;
      severity = ErrorSeverity.MEDIUM;
      userMessage = 'Unable to process the response.';
      recoverySuggestions = [
        'Try again with different content',
        'Simplify your request',
      ];
    }
    // Office.js errors
    else if (errorMsg.includes('office') || errorMsg.includes('word') || errorMsg.includes('document')) {
      category = ErrorCategory.OFFICE_JS;
      severity = ErrorSeverity.HIGH;
      userMessage = 'Unable to access the document.';
      recoverySuggestions = [
        'Ensure you have selected text in the document',
        'Check document permissions',
        'Try refreshing the add-in',
      ];
    }
    // API errors
    else if (errorMsg.includes('api') || errorMsg.includes('500') || errorMsg.includes('502') || errorMsg.includes('503')) {
      category = ErrorCategory.API;
      severity = ErrorSeverity.HIGH;
      userMessage = 'The service is temporarily unavailable.';
      recoverySuggestions = [
        'Try again in a few moments',
        'Contact support if the issue persists',
      ];
    }

    return {
      message: error.message,
      userMessage,
      category,
      severity,
      code: (error as { code?: string }).code,
      status: (error as { status?: number }).status,
      context,
      timestamp,
      stack: error.stack,
      metadata,
      recoverySuggestions,
    };
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      message: error,
      userMessage: error,
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.MEDIUM,
      context,
      timestamp,
      metadata,
      recoverySuggestions: ['Try again', 'Contact support if the issue persists'],
    };
  }

  // Handle unknown error types
  return {
    message: 'An unknown error occurred',
    userMessage: 'An unexpected error occurred. Please try again.',
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.MEDIUM,
    context,
    timestamp,
    metadata,
    recoverySuggestions: ['Try again', 'Refresh the page', 'Contact support if the issue persists'],
  };
};

/**
 * Logs errors consistently with structured data
 */
export const logError = (
  error: AppError,
  consoleOutput: boolean = true
): void => {
  const logData = {
    timestamp: error.timestamp,
    context: error.context || 'Unknown',
    category: error.category,
    severity: error.severity,
    message: error.message,
    userMessage: error.userMessage,
    code: error.code,
    status: error.status,
    metadata: error.metadata,
    stack: error.stack,
  };

  if (consoleOutput) {
    // Use different console methods based on severity
    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        console.error(`[${error.context}] ${error.category.toUpperCase()} ERROR:`, logData);
        break;
      case ErrorSeverity.MEDIUM:
        console.warn(`[${error.context}] ${error.category.toUpperCase()} WARNING:`, logData);
        break;
      case ErrorSeverity.LOW:
        console.info(`[${error.context}] ${error.category.toUpperCase()} INFO:`, logData);
        break;
    }
  }

  // F-16: Send error events to telemetry
  try {
    trackEvent('error', logData.context || 'Unknown', logData.category, {
      message: logData.message,
      severity: logData.severity,
      code: logData.code,
      status: logData.status,
    });
  } catch {
    // Telemetry should never break the main app
  }
};

/**
 * Gets a user-friendly error message with recovery suggestions
 */
export const getUserErrorMessage = (error: AppError): string => {
  let message = error.userMessage;

  if (error.recoverySuggestions && error.recoverySuggestions.length > 0) {
    message += '\n\nSuggestions:\n' + error.recoverySuggestions.map(s => `• ${s}`).join('\n');
  }

  return message;
};

/**
 * Handles errors consistently across features
 * Returns a structured error object for display
 */
export const handleError = (
  error: unknown,
  context: string,
  metadata?: Record<string, unknown>
): AppError => {
  const appError = createAppError(error, context, metadata);
  logError(appError);
  return appError;
};

/**
 * Error recovery helper - determines if an error is recoverable
 */
export const isRecoverableError = (error: AppError): boolean => {
  return error.category === ErrorCategory.TIMEOUT ||
    error.category === ErrorCategory.NETWORK ||
    error.category === ErrorCategory.RATE_LIMIT ||
    error.severity === ErrorSeverity.LOW ||
    error.severity === ErrorSeverity.MEDIUM;
};

/**
 * Determines if an error should trigger a retry
 */
export const shouldRetry = (error: AppError): boolean => {
  return error.category === ErrorCategory.NETWORK ||
    error.category === ErrorCategory.TIMEOUT ||
    error.category === ErrorCategory.RATE_LIMIT ||
    (error.status !== undefined && error.status >= 500);
};

